import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, arrayOf, extend, bool
} from '@thd-nucleus/data-sources';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import {
  ProductPod,
  PodFooter,
  PodHeader,
  PodSection,
  PodSpacer,
  PodRowToColumn,
  PodRow,
  PodTapper,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  ProductCustomOptions,
  ProductIdentifier,
  BundleDataModel,
} from '@thd-olt-component-react/product-pod';
import { ProductSeeSimilarItems, ViewSimilarIcon } from '@thd-olt-component-react/product-pod-group';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { Promotion } from '@thd-olt-component-react/promotion';
import { useClickInteractionListener } from '@thd-olt-functional/utils/';
import { sendPodTapperAnalytics } from '@thd-olt-component-react/product-pod/dist/components/productPodUtils';

export const CustomProductPodResponsive = ({
  itemId,
  parentId,
  storeId,
  position,
  onClick,
  hoverDelay,
  url,
  samplesLoading,
  hasInStoreFilter,
  parent,
  impProductPodData,
  hasSameDayFilter,
  isMobileOrConsumerApp,
  isFulfillmentFilterAndMsgEnabled,
  showViewSimilarButton
}) => {
  const superSkuChangedItemIdsRef = useRef([]);
  const theme = useTheme(CustomProductPodResponsive);
  const { hideAddToListIcon, hidePickupFulfillment } = theme.props;
  const showFulfillmentPodStore = isFulfillmentFilterAndMsgEnabled ? !hasSameDayFilter : true;
  const showFulfillmentPodShipping = isFulfillmentFilterAndMsgEnabled ? !hasInStoreFilter : true;

  // -----------------
  // Analytics - New-Tab Click Tracker
  // -----------------

  const productClickData = {
    podAction: 'product pod',
    podAnchorSku: itemId,
    target: '_self',
    parent: 'plp'
  };

  const sendAnalytics = () => {
    if (!window) return;
    window.LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.click', productClickData); // analytic & ad-server
  };

  const useNodeRef = () => {
    const [node, setNode] = useState(null); // <HTMLElement | null>

    /**
     * Callback ref to be called by React whenever the ref gets attached
     * to a different node or becomes null.
     * @see https://legacy.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
     */
    const callbackRef = useCallback((value) => {
      setNode(value);
    }, []);

    return [callbackRef, node];
  };

  const [productPodNodeRef, productPodNode] = useNodeRef();

  useClickInteractionListener({
    callbackFn: sendAnalytics,
    targetNode: productPodNode,
    itemId,
    sponsoredId: itemId,
    trackSource: '',
    slotId: '',
    position,
    plaLocation: ''
  });

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      className="product-pod"
      data-testid="product-pod"
    >
      <ProductPod
        analyticsData={{ parent, position }}
        analyticImpressionData={impProductPodData}
        hoverDelay={hoverDelay}
        hasSSKU
        itemId={itemId}
        storeId={storeId}
        padding="sui-p-2 sm:sui-pt-2 sm:sui-px-4 sm:sui-pb-4"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-10"
              href={url}
              aria-label="Link"
            />
            <div className="sui-flex sui-flex-col sui-h-full">
              <PodSpacer padding={['BOTTOM']}>
                <PodHeader noPadding>
                  <PodRow noPadding>
                    <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    <ProductSponsored itemId={pod.itemId} position={position} />
                  </PodRow>
                  {!hideAddToListIcon && (
                    <PodTapper fitWidth zIndex>
                      <PodRow rowReverse noPadding>
                        <AddToList itemId={pod.itemId} showIconButton />
                      </PodRow>
                    </PodTapper>
                  )}
                </PodHeader>
              </PodSpacer>
              <PodSection
                id="Slot-2"
                display="sui-grid"
                gap="sui-gap-0"
                noPadding
              >
                <PodRowToColumn
                  noPadding
                  grid="sui-grid-cols-1"
                >
                  <PodTapper zIndex>
                    <ProductImage
                      itemId={pod.itemId}
                      onClick={onClick}
                      showSecondaryImage={false}
                      dualImageSwipe={false}
                    />
                  </PodTapper>
                  <PodSection
                    display="sui-block sui-w-full"
                    noPadding
                    padding="sui-pt-2"
                  >
                    <PodTapper
                      zIndex
                      aPointer
                      buttonPointer
                      cursorPointer
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <ProductSample itemId={pod.itemId} />
                      <SuperSkuMiniSwatches
                        storeId={storeId}
                        itemId={pod.itemId}
                        onChange={(clickData) => {
                          if (!superSkuChangedItemIdsRef.current.includes(clickData.itemId)) {
                            superSkuChangedItemIdsRef.current = [
                              ...superSkuChangedItemIdsRef.current,
                              clickData.itemId
                            ];
                          }
                          pod.onChange(clickData);
                        }}
                        onHover={pod.onSSKUHover}
                        componentImpressionData={{
                          name: 'ProductPod',
                          component: 'ProductPod',
                          position: position + 1,
                          type: 'product',
                          anchorSku: parentId
                        }}
                        isImpressionsRequired
                        removeStaticHeight
                        hideComponentWhenEmpty
                      />
                      <ProductServiceAddOns itemId={pod.itemId} />
                    </PodTapper>
                  </PodSection>
                  {showViewSimilarButton?.isViewSimilarTopButtonEnabled && (
                    <PodSection
                      display="sui-block sui-w-full"
                      noPadding
                      zIndex
                      fitWidth
                    >
                      <div className="sui-flex sui-items-center sui-z-20">
                        <ViewSimilarIcon
                          itemId={pod.itemId}
                          products={showViewSimilarButton?.productArray}
                          storeId={storeId}
                        />
                      </div>
                    </PodSection>
                  )}
                  <PodSection
                    display="sui-grid"
                    gap="sui-gap-0"
                    padding="sui-mb-auto sui-px-1"
                    alignTop
                    noPadding
                  >
                    <PodTapper fitWidth zIndex>
                      <PodSpacer margin="md:sui-mt-1">
                        <ProductHeader
                          brand="inline"
                          brandLineHeight="tight"
                          brandFontSize="sm"
                          productNameLineHeight="tight"
                          itemId={pod.itemId}
                          brandTitleMaxLine={isMobileOrConsumerApp ? 4 : 3}
                          titleMaxLine={isMobileOrConsumerApp ? 3 : 2}
                          disableShopThisCollection
                          onClick={onClick}
                        />
                      </PodSpacer>
                    </PodTapper>
                    <PodTapper zIndex>
                      <ProductRating itemId={pod.itemId} onClick={onClick} />
                    </PodTapper>
                    <ProductIdentifier itemId={pod.itemId} type="model" />
                  </PodSection>
                </PodRowToColumn>
                <PodSection noPadding alignTop columnAlign padding="sui-px-0">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['T2', 'B2']}
                      disable={pod.productSubType === 'HDQC'}
                    >
                      <Price
                        clsRemediation={{ placeholders: true, preservePlaceholders: true }}
                        disableOuterSpacing
                        itemId={pod.itemId}
                        large={false}
                        storeId={storeId}
                        displayEachUom={false}
                        onSavingsCenterToggle={pod.onSavingsCenterToggle}
                        hideForProductTypes={['HDQC']}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                <PodSection noPadding alignTop columnAlign padding="sui-px-0">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['T2', 'B2']}
                    >
                      <Promotion
                        itemId={pod.itemId}
                        hideConditionalPromoDescription
                        pageType={parent.toUpperCase()}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
              </PodSection>
              <PodFooter noPadding margin="sui-mt-auto">
                <PodSection columnAlign noPadding grid alignEnd padding="sui-px-0">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['B2']}
                      disable={pod.productSubType === 'HDQC'}
                    >
                      { (!hidePickupFulfillment && showFulfillmentPodStore)
                      && (
                        <FulfillmentPodStore
                          itemId={pod.itemId}
                          storeId={storeId}
                          hideForChooseYourOptions
                        />
                      )}
                      { showFulfillmentPodShipping && (
                        <FulfillmentPodShipping
                          itemId={pod.itemId}
                          storeId={storeId}
                          hasSameDayFilter={hasSameDayFilter}
                          showFasterPodSpacing
                        />
                      )}
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                <PodSection
                  display="sui-grid"
                  gap="sui-gap-0"
                  noPadding
                  alignEnd
                >
                  <PodTapper zIndex>
                    <PodRow noPadding>
                      <div className="sui-flex-1">
                        <ProductATC
                          hasInStoreFilter={hasInStoreFilter}
                          storeId={storeId}
                          itemId={pod.itemId}
                          checkGeneric
                          outline
                        />
                      </div>
                      <ProductSeeSimilarItems
                        itemId={pod.itemId}
                        position={position}
                        productPodRef={pod.ref}
                        storeId={storeId}
                      />
                      <PodSpacer disable={pod.productSubType !== 'HDQC'}>
                        <NonBuyableQuoteCenter
                          itemId={pod.itemId}
                          checkForProductSubType={['HDQC']}
                        />
                      </PodSpacer>
                      {showViewSimilarButton?.isViewSimilarBottomButtonEnabled && (
                        <div className="sui-flex sui-items-center sui-pl-2">
                          <ViewSimilarIcon
                            itemId={pod.itemId}
                            products={showViewSimilarButton?.productArray}
                            storeId={storeId}
                          />
                        </div>
                      )}
                      <div className="sui-flex sui-items-center sui-self-center">
                        <OrderSample
                          disableSampleCall={
                            samplesLoading
                            || (pod.itemId !== itemId && !superSkuChangedItemIdsRef.current.includes(pod.itemId))
                          }
                          itemId={pod.itemId}
                          showTitle={false}
                          showIconOnly
                          link
                        />
                      </div>
                    </PodRow>
                  </PodTapper>
                </PodSection>
              </PodFooter>
            </div>
          </>
        )}
      />
    </div>
  );
};
CustomProductPodResponsive.displayName = 'CustomProductPodResponsive';

CustomProductPodResponsive.themeProps = {
  hideAddToListIcon: PropTypes.bool,
  hidePickupFulfillment: PropTypes.bool
};

CustomProductPodResponsive.defaultThemeProps = {
  hideAddToListIcon: false,
  hidePickupFulfillment: false
};

CustomProductPodResponsive.propTypes = {
  itemId: PropTypes.string.isRequired,
  parentId: PropTypes.string,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  samplesLoading: PropTypes.bool.isRequired,
  hoverDelay: PropTypes.number,
  onClick: PropTypes.func,
  hasInStoreFilter: PropTypes.bool,
  impProductPodData: PropTypes.shape({}),
  parent: PropTypes.string,
  hasSameDayFilter: PropTypes.bool,
  isMobileOrConsumerApp: PropTypes.bool,
  isFulfillmentFilterAndMsgEnabled: PropTypes.bool,
  showViewSimilarButton: PropTypes.shape({
    isViewSimilarTopButtonEnabled: PropTypes.bool,
    isViewSimilarBottomButtonEnabled: PropTypes.bool,
    productArray: PropTypes.arrayOf(PropTypes.shape({}))
  }),
};

CustomProductPodResponsive.defaultProps = {
  onClick: () => {},
  hasInStoreFilter: false,
  parent: 'plp',
  hoverDelay: 500,
  impProductPodData: null,
  hasSameDayFilter: false,
  isMobileOrConsumerApp: false,
  isFulfillmentFilterAndMsgEnabled: false,
  parentId: null,
  showViewSimilarButton: {
    fsisViewSimilarButtonPositionAboveABTest: false,
    fsisViewSimilarButtonPositionBelowABTest: false,
    productArray: []
  },
};

CustomProductPodResponsive.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      details: shape({
        collection: shape({
          collectionId: string()
        }),
        highlights: arrayOf(string())
      }),
      info: shape({
        productDepartmentId: string(),
        paintBrand: string(),
        dotComColorEligible: bool(),
      }),
      pricing: params({ storeId: string() }).shape({
        promotion: shape({
          dates: shape({
            start: string(),
            end: string()
          })
        })
      }),
    })
  },
  ProductPod,
  ProductBadge,
  ProductSponsored,
  AddToList,
  ProductImage,
  ProductSample,
  SuperSkuMiniSwatches,
  ProductServiceAddOns,
  ProductHeader,
  ProductRating,
  ProductIdentifier,
  Promotion,
  Price,
  PriceClearanceDataModel,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductATC,
  ProductSeeSimilarItems,
  NonBuyableQuoteCenter,
  OrderSample,
  ProductCustomOptions,
  BundleDataModel,
  ViewSimilarIcon
);
